import {
    Anchor,
    Body1,
    Box,
    Checkbox,
    CollapsibleCard,
    Currency,
    FilledCard,
    Flex,
    Modal,
    Stack,
} from '@phx/design-system';
import { useDisclosure } from '@phx/design-system/hooks';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type {
    AuthorizedMedicationInfoFragment,
    CoverageFlags,
    DigitallyReceivedPrescriberOrderInfoFragment,
    MedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import { PrescriberOrderDetails } from '../../../routes/orders/details/PrescriberOrderDetails';
import { NotCoveredModal } from '../../common/drug-coverage-alerts/NotCoveredModal';
import { PARequiredModal } from '../../common/drug-coverage-alerts/PARequiredModal';
import { MissingBenefitsCard } from '../../prior-authorization/components/MissingBenefitsCard';

import { DAWModal } from './DAWModal';
import { PatientProductDetailsHeader } from './PatientProductDetailsHeader';

export type PatientProductDetailsV2Props = {
    patientName?: string;
    productDescription?: string;
    quantity?: number | null;
    prescriberOrder?: DigitallyReceivedPrescriberOrderInfoFragment;
    authorizedMedicationSavings: number;
    authorizedMed: AuthorizedMedicationInfoFragment;
    prescribedMed: MedicationInfoFragment | UnknownMedicationInfoFragment;
    setOverrideOfferList: (
        override: 'authorized' | 'prescribed' | 'default'
    ) => void;
    showAuthorizedOffersChecked: boolean;
    coverageFlags?: CoverageFlags;
};

export const PatientProductDetailsV2 = ({
    patientName,
    productDescription,
    quantity,
    prescriberOrder,
    authorizedMedicationSavings,
    authorizedMed,
    prescribedMed,
    setOverrideOfferList,
    showAuthorizedOffersChecked,
    coverageFlags,
}: PatientProductDetailsV2Props) => {
    const { t } = useTranslation();
    const [
        isGenericModalOpen,
        { close: closeGenericModal, toggle: toggleGenericModal },
    ] = useDisclosure(false);
    const [isPARequiredModalOpen, { toggle: togglePARequiredModal }] =
        useDisclosure(false);
    const [isNotCoveredModalOpen, { toggle: toggleNotCoveredModal }] =
        useDisclosure(false);
    const [isDAWModalOpen, { close: closeDAWModal, toggle: toggleDAWModal }] =
        useDisclosure(false);

    const isSwapped = authorizedMed.id !== prescribedMed.id;

    if (!prescriberOrder) {
        return <MissingBenefitsCard />;
    }

    const CardHeader = (
        <PatientProductDetailsHeader
            patientName={patientName}
            productDescription={productDescription}
            quantity={quantity}
            prescriberOrder={prescriberOrder}
            coverageFlags={coverageFlags}
            togglePARequiredModal={togglePARequiredModal}
            toggleNotCoveredModal={toggleNotCoveredModal}
        />
    );

    const toggleCheckbox = useCallback(() => {
        if (showAuthorizedOffersChecked) {
            setOverrideOfferList('prescribed');
        } else {
            setOverrideOfferList('authorized');
        }
    }, [showAuthorizedOffersChecked, setOverrideOfferList]);

    // TODO: This currently does not match designs as there is padding around the footer when it should span the entire width of the collapsible card.
    // See user story 905: https://dev.azure.com/Prescryptive-Health/Main/_workitems/edit/905
    const CardFooter = (
        <FilledCard
            color={showAuthorizedOffersChecked ? 'success' : 'gray'}
            sharp
        >
            <Flex>
                <Box flex={1}>
                    {showAuthorizedOffersChecked ? (
                        <Trans
                            i18nKey="drx.product.details.savingWithGeneric"
                            values={{
                                genericName: authorizedMed.name,
                            }}
                        >
                            <Stack gap="xs">
                                <Body1 bold>
                                    {'Saving up to '}
                                    <Currency
                                        amount={authorizedMedicationSavings}
                                    />
                                    {' with '}
                                    <Anchor onClick={toggleGenericModal}>
                                        generic
                                    </Anchor>
                                </Body1>
                                <Body1>
                                    {'Prices shown for '}
                                    {authorizedMed.name}
                                </Body1>
                            </Stack>
                        </Trans>
                    ) : (
                        <Trans i18nKey="drx.product.details.saveWithGeneric">
                            <Stack gap="xs">
                                <Body1 bold>
                                    {'Save up to '}
                                    <Currency
                                        amount={authorizedMedicationSavings}
                                    />
                                    {' with '}
                                    <Anchor onClick={toggleGenericModal}>
                                        generic
                                    </Anchor>
                                </Body1>
                            </Stack>
                        </Trans>
                    )}
                </Box>
                <Box>
                    <Checkbox
                        checked={showAuthorizedOffersChecked}
                        onChange={toggleCheckbox}
                    />
                </Box>
            </Flex>
        </FilledCard>
    );

    return (
        <>
            <CollapsibleCard
                variant="elevated"
                header={CardHeader}
                footer={isSwapped && CardFooter}
            >
                <PrescriberOrderDetails prescriberOrder={prescriberOrder} />
                {prescriberOrder.dispenseAsWritten && (
                    <FilledCard color="gray" sharp>
                        <Trans i18nKey="drx.product.details.daw.unknownSavings">
                            <Body1>
                                {'The healthcare provider indicated "'}
                                <Anchor onClick={toggleDAWModal}>
                                    Dispense As Written
                                </Anchor>
                                {
                                    '" for the brand. Consult the provider about switching to the '
                                }
                                <Anchor onClick={toggleGenericModal}>
                                    generic
                                </Anchor>
                                {' to save.'}
                            </Body1>
                        </Trans>
                    </FilledCard>
                )}
            </CollapsibleCard>
            <Modal
                opened={isGenericModalOpen}
                onClose={closeGenericModal}
                title={t('drx.product.details.modals.generic.title')}
            >
                <Body1>{t('drx.product.details.modals.generic.body')}</Body1>
            </Modal>
            <DAWModal close={closeDAWModal} isOpen={isDAWModalOpen} />
            <PARequiredModal
                opened={isPARequiredModalOpen}
                close={togglePARequiredModal}
            />
            <NotCoveredModal
                opened={isNotCoveredModalOpen}
                close={toggleNotCoveredModal}
            />
        </>
    );
};
